import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Field } from 'react-final-form'
import { Box, FormHelperText, Grid, Paper, Typography } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DescriptionIcon from '@mui/icons-material/Description'

const baseStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  transition: 'border .3s ease-in-out, background-color .3s',
}

const activeStyle = {
  borderColor: '#2196f3',
}

const acceptStyle = {
  borderColor: '#00e676',
}

const rejectStyle = {
  borderColor: '#ff1744',
}

const hoverStyle = {
  backgroundColor: '#eeeeee',
}

const FileField = ({
  name,
  required,
  validate = () => {},
  helperText,
  accept,
  multiple = false,
  noPreview = false,
  ...props
}) => {
  const [files, setFiles] = useState([])

  const thumbs = (
    <Grid container spacing={2} direction='row' justifyContent={multiple ? undefined : 'center'}>
      {files.map((file, index) => (
        <Grid item xs={6} sm={4} md={3} lg={2} key={file.name}>
          <Box display='flex' flexDirection='column' alignItems='center'>
            <Paper square>
              {file.preview ? (
                <img
                  style={{ minWidth: 50, maxWidth: '100%' }}
                  src={file.preview}
                  alt={file.name}
                />
              ) : (
                <DescriptionIcon fontSize='large' />
              )}
            </Paper>
            <Typography align='center' variant='caption'>
              {file.name}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  )

  // clean up
  useEffect(() => {
    return () => {
      files.forEach((file) => URL.revokeObjectURL(file.preview))
    }
  }, [files])
  return (
    <Field name={name} required={required}>
      {({ input, meta }) => {
        return (
          <FieldItem
            input={input}
            meta={meta}
            name={name}
            required={required}
            validate={validate}
            helperText={helperText}
            accept={accept}
            multiple={multiple}
            noPreview={noPreview}
            setFiles={setFiles}
            files={files}
            thumbs={thumbs}
            {...props}
          />
        )
      }}
    </Field>
  )
}

export default FileField

const FieldItem = ({
  input: { value, onChange, ...input },
  meta,
  name,
  required,
  validate = () => {},
  helperText,
  accept,
  multiple = false,
  noPreview = false,
  files,
  setFiles,
  thumbs,
  ...props
}) => {
  useEffect(() => {
    onDrop(value)
  }, [])

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const newFiles = multiple ? [...value, ...acceptedFiles] : [acceptedFiles[0]]
      setFiles(
        newFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      )
      onChange(newFiles)
    }
    validate?.()
  }, [])

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    accept,
  })

  const [hover, setHover] = useState(false)

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
      ...(hover ? hoverStyle : {}),
    }),
    [hover, isDragActive, isDragReject, isDragAccept]
  )

  return (
    <div>
      <div
        {...getRootProps({ style })}
        onMouseOver={() => {
          setHover(true)
        }}
        onMouseOut={() => {
          setHover(false)
        }}
      >
        <input
          {...getInputProps({ ...input })}
          type='file'
          onChange={(e) => {
            onDrop([...e.target.files]?.filter((file) => !accept || accept?.includes(file.type)))
          }}
        />
        <Typography variant='h3'>
          <CloudUploadIcon fontSize='inherit' />
        </Typography>
        <Typography variant='h6' align='center'>
          {multiple ? 'Choose files...' : 'Choose a file...'}
        </Typography>
        {!noPreview && files?.length > 0 && (
          <Box p={1} width='100%'>
            <Paper variant='outlined'>
              <Box p={1} width='100%'>
                {thumbs}
              </Box>
            </Paper>
          </Box>
        )}
      </div>
      <FormHelperText error={meta.error && meta.touched}>
        {meta.error && meta.touched ? meta.error[0] : helperText}
      </FormHelperText>
    </div>
  )
}
